<template>
  <v-container fill-height class="pa-4">
    <v-row no-gutters justify="center">
      <v-col cols="12" sm="10" md="12" lg="8">
        <!-- Header -->
        <div class="header-section mb-6">
          <div class="black-header pa-4 mb-4">
            <h1 class="text-h4 font-weight-bold white--text">
              {{ $t("checkout." + datas.service.serviceName) }} - ({{
                $appFormatters.formatDateTimeRead(
                  datas.customerReadyTime,
                  datas.isAsap
                )
              }})
            </h1>
          </div>
          <h2 class="text-h5 font-weight-bold mb-2 text-center">
            {{ $t("thanks.thank_you_title") }}
          </h2>
          <p class="text-subtitle-1 text-center">
            {{ getStatusMessage }}
          </p>
        </div>

        <!-- Progress Steps -->
        <div class="steps-section mb-6">
          <v-stepper v-model="step" alt-labels class="elevation-0">
            <v-stepper-header class="elevation-0 steps-container">
              <v-stepper-step :complete="step > 1" step="1" color="primary">
                <span class="step_title" v-text="$t('thanks.step1')" />
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step :complete="step > 2" step="2" color="primary">
                <span
                  class="step_title"
                  v-text="
                    datas.service.serviceSlug === 'pickup-service'
                      ? $t('thanks.step2Pickup')
                      : $t('thanks.step2')
                  "
                />
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step
                :complete="step > 3"
                step="3"
                color="primary"
                :rules="[() => datas.orderStatus !== 'OrderIsCancelled']"
              >
                <template #default>
                  <span class="step_title">
                    {{
                      datas.orderStatus === "OrderIsCancelled"
                        ? $t("thanks.order_canceled")
                        : $t("thanks.step3")
                    }}
                  </span>
                </template>
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step step="4" color="primary">
                <span class="step_title" v-text="$t('thanks.step4')" />
              </v-stepper-step>
            </v-stepper-header>
          </v-stepper>
        </div>

        <!-- Restaurant Info and Overview in two columns -->
        <v-row class="mb-6">
          <!-- Restaurant Info -->
          <v-col cols="12" md="8">
            <div class="info-section">
              <div class="restaurant-detail-item">
                <h3 class="text-h5 font-weight-bold">
                  {{ $t("thanks.restaurant_info_title") }}
                </h3>
                <p
                  v-for="(item, index) in [
                    datas.Store.storeName,
                    datas.Store.storeAddress,
                    datas.Store.phoneNumber,
                  ]"
                  :key="`item-${index}`"
                  class="text-subtitle-1 mb-1"
                >
                  {{ item }}
                </p>
              </div>

              <!-- Order Information -->
              <div class="restaurant-detail-item">
                <h3 class="text-h5 font-weight-bold">
                  {{ $t("thanks.order_info_title") }}
                </h3>
                <p class="text-subtitle-1 mb-2">
                  {{ $t("thanks.order_number") }}
                  <strong>#{{ datas.publicRef }}</strong>
                </p>
              </div>

              <!-- Payment Status -->
              <div class="restaurant-detail-item">
                <h3 class="text-h5 font-weight-bold">
                  {{ $t("thanks.payment_status_title") }}
                </h3>
                <p class="text-subtitle-1 mb-2">
                  {{
                    datas.paymentStatus === 1
                      ? $t("thanks.payment_status_paid_online")
                      : $t("thanks.payment_status_description")
                  }}
                </p>
              </div>

              <!-- Help Section -->
              <div class="restaurant-detail-item">
                <h3 class="text-h5 font-weight-bold">
                  {{ $t("thanks.need_help_title") }}
                </h3>
                <p class="text-subtitle-1">
                  {{ $t("thanks.need_help_text") }}
                  <strong>#{{ datas.publicRef }}</strong>
                </p>
              </div>
            </div>
          </v-col>

          <!-- Overview -->
          <v-col cols="12" md="4">
            <v-card outlined class="overview-card">
              <v-card-title
                class="text-h6 font-weight-bold px-6 pt-5 pb-3 overview-title"
                >{{ $t("thanks.overview_title") }}</v-card-title
              >
              <v-card-text class="px-6 pt-2 pb-5">
                <v-container fluid pa-0 ma-0>
                  <!-- Order Items -->
                  <div
                    v-for="(item, index) in datas.cart[0].items"
                    :key="'row-' + index"
                  >
                    <v-row class="pa-0 ma-0">
                      <div class="mt-2 pa-0 ma-0" style="width: 70%">
                        <span>{{ item.quantity }} X </span>
                        <span>
                          {{ item.productName
                          }}{{
                            item.productVariationName
                              ? " ( " + item.productVariationName + " )"
                              : ""
                          }}
                        </span>
                      </div>
                      <div
                        style="width: 30%; text-align: right; text-wrap: nowrap"
                        class="mt-2"
                      >
                        <span>
                          {{
                            $appFormatters.formatMoney(
                              item.itemTotalPrice,
                              datas.Store.currency
                            )
                          }}
                        </span>
                      </div>

                      <!-- Addons -->
                      <div class="main-addons-container">
                        <div
                          v-for="mainAddon in item.groupedAddons"
                          :key="'addon-' + mainAddon.mainAddonName"
                        >
                          <div
                            :key="'addon-' + mainAddon.mainAddonName"
                            class="pa-0 ma-0"
                            style="width: 100%"
                          >
                            <span
                              v-if="mainAddon.subAddons.length === 1"
                              class="product-addons-list"
                            >
                              {{ mainAddon.mainAddonName }}:
                              {{ mainAddon.subAddons[0].addonName }}
                              {{
                                mainAddon.subAddons[0].addonTotalPrice > 0
                                  ? " + " +
                                    $appFormatters.formatMoney(
                                      mainAddon.subAddons[0].addonTotalPrice,
                                      datas.Store.currency
                                    )
                                  : ""
                              }}
                            </span>
                            <div
                              v-if="
                                mainAddon.subAddons &&
                                mainAddon.subAddons.length > 1
                              "
                            >
                              <span class="product-variation">
                                {{ mainAddon.mainAddonName }}:
                                <template
                                  v-if="mainAddon.subAddons.length === 1"
                                >
                                  {{ mainAddon.subAddons[0].addonName }}
                                  {{
                                    mainAddon.subAddons[0].addonTotalPrice > 0
                                      ? " + " +
                                        $appFormatters.formatMoney(
                                          mainAddon.subAddons[0]
                                            .addonTotalPrice,
                                          datas.Store.currency
                                        )
                                      : ""
                                  }}
                                </template>
                              </span>

                              <!-- Multiple subAddons case -->
                              <template v-if="mainAddon.subAddons.length > 1">
                                <div class="sub-addons-container">
                                  <p
                                    v-for="addon in mainAddon.subAddons"
                                    :key="'subaddon-' + addon.id"
                                    class="product-addons-list mb-0"
                                  >
                                    {{ addon.quantity }} X {{ addon.addonName }}
                                    {{
                                      addon.addonTotalPrice > 0
                                        ? " + " +
                                          $appFormatters.formatMoney(
                                            addon.addonTotalPrice,
                                            datas.Store.currency
                                          )
                                        : ""
                                    }}
                                  </p>
                                </div>
                              </template>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="divider-details mt-2" style="width: 100%">
                        <v-divider></v-divider>
                      </div>
                    </v-row>
                  </div>

                  <!-- Bag Cost -->
                  <v-row class="pa-0 ma-0">
                    <div class="pa-0 ma-0" style="width: 70%">
                      <span>{{ $t("checkout.checkout_extra_bag_cost") }}</span>
                    </div>
                    <div style="width: 30%; text-align: right">
                      <span>
                        {{
                          $appFormatters.formatMoney(
                            datas.Store.configs.extraBagCost,
                            datas.Store.currency
                          )
                        }}
                      </span>
                    </div>
                    <div
                      v-if="datas.serviceFee === null || datas.serviceFee === 0"
                      class="divider-details"
                      style="width: 100%"
                    >
                      <v-divider></v-divider>
                    </div>
                  </v-row>

                  <!-- Service Fee -->
                  <v-row
                    v-if="datas.serviceFee !== null || datas.serviceFee > 0"
                    class="pa-0 ma-0"
                  >
                    <div class="pa-0 ma-0" style="width: 70%">
                      <span>{{ $t("checkout.serviceFee") }}</span>
                    </div>
                    <div style="width: 30%; text-align: right">
                      <span>
                        {{
                          $appFormatters.formatMoney(
                            datas.serviceFee,
                            datas.Store.currency
                          )
                        }}
                      </span>
                    </div>
                    <div class="divider-details" style="width: 100%">
                      <v-divider></v-divider>
                    </div>
                  </v-row>

                  <!-- Delivery Fee -->
                  <v-row
                    v-if="datas.service.serviceSlug === 'delivery-service'"
                    class="pa-0 ma-0"
                  >
                    <div style="width: 100%; text-align: right">
                      <span
                        v-if="
                          datas.deliveryFee &&
                          parseFloat(datas.deliveryFee) > 0.0
                        "
                      >
                        {{ $t("general.delivery") }}:
                        {{
                          $appFormatters.formatMoney(
                            datas.deliveryFee,
                            datas.Store.currency
                          )
                        }}
                      </span>
                      <span
                        v-if="
                          datas.deliveryFee &&
                          parseFloat(datas.deliveryFee) === 0.0
                        "
                      >
                        {{ $t("general.delivery") }}: Free
                      </span>
                    </div>
                  </v-row>

                  <!-- Discount -->
                  <v-row v-if="datas.offerAmount > 0" class="pa-0 ma-0">
                    <div style="width: 100%; text-align: right">
                      <span>
                        {{ $t("offers.discount") }}: -
                        {{
                          $appFormatters.formatMoney(
                            datas.offerAmount,
                            datas.Store.currency
                          )
                        }}
                      </span>
                    </div>
                  </v-row>

                  <!-- Credits -->
                  <v-row v-if="datas.creditsAmount > 0" class="pa-0 ma-0">
                    <div style="width: 100%; text-align: right">
                      <span>
                        {{ $t("customerProfile.credits") }}: -
                        {{
                          $appFormatters.formatMoney(
                            datas.creditsAmount,
                            datas.Store.currency
                          )
                        }}
                      </span>
                    </div>
                  </v-row>

                  <!-- VAT -->
                  <v-row class="pa-0 ma-0">
                    <div style="width: 100%; text-align: right">
                      <span>
                        VAT({{ datas.taxPercent }}%):
                        {{
                          $appFormatters.formatMoney(
                            datas.taxAmount,
                            datas.Store.currency
                          )
                        }}
                      </span>
                    </div>
                  </v-row>

                  <!-- Total -->
                  <v-row class="pa-0 ma-0">
                    <div
                      class="text-h5 font-weight-bold text-right"
                      style="width: 100%"
                    >
                      <span>
                        Total:
                        {{
                          $appFormatters.formatMoney(
                            datas.grandTotal,
                            datas.Store.currency
                          )
                        }}
                      </span>
                    </div>
                  </v-row>

                  <!-- Action Buttons -->
                  <v-row class="mt-6">
                    <v-col cols="12" class="pa-0">
                      <v-btn
                        block
                        color="grey lighten-2"
                        class="text-none mb-3 black--text"
                        elevation="0"
                        height="40"
                        @click="downloadInvoice"
                      >
                        {{ $t("thanks.download_invoice") }}
                      </v-btn>
                      <v-btn
                        block
                        color="black"
                        class="text-none white--text"
                        elevation="0"
                        height="40"
                        @click="sendReceipt"
                      >
                        {{ $t("thanks.send_receipt") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "InfoSection",
  props: {
    datas: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    step: 1,
  }),
  computed: {
    getStatusMessage() {
      if (this.datas.orderStatus === "OrderIsCancelled") {
        return this.$t("thanks.order_cancelled_description");
      }
      switch (this.datas.customerOrderStatus) {
        case "Waiting":
          return this.$t("thanks.received_description");
        case "ConfirmedByStore":
          return this.$t("thanks.confirmed_description");
        case "OnItsWay":
          return this.$t("thanks.ready_description");
        case "Completed":
          return this.$t("thanks.completed_description");
        default:
          return this.$t("thanks.received_description");
      }
    },
  },
  mounted() {
    this.setInitialStep();
  },
  methods: {
    setInitialStep() {
      switch (this.datas.customerOrderStatus) {
        case "Waiting":
          this.step = 1;
          break;
        case "ConfirmedByStore":
          this.step = 2;
          break;
        case "OnItsWay":
          this.step = 3;
          break;
        case "Completed":
          this.step = 4;
          break;
        default:
          this.step = 1;
      }
    },
    setStatus() {
      this.setInitialStep();
    },
    async downloadInvoice() {
      try {
        const { data } = await axios.get(
          `/app/v2/order/${this.datas.orderRef}`,
          { responseType: "blob" }
        );
        const blob = new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoice-${this.datas.orderRef}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: this.$t("errors.error_alert_title"),
          text:
            error.response?.data?.message || this.$t("errors.error_alert_text"),
          reverseButtons: true,
          confirmButtonText: "ok",
        });
      }
    },
    async sendReceipt() {
      try {
        const { data } = await axios.post(
          `/app/v2/order/${this.datas.orderRef}`
        );
        Swal.fire({
          icon: "success",
          title: data.message,
          reverseButtons: true,
          confirmButtonText: "ok",
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: this.$t("errors.error_alert_title"),
          text:
            error.response?.data?.message || this.$t("errors.error_alert_text"),
          reverseButtons: true,
          confirmButtonText: "ok",
        });
      }
    },
  },
  beforeUnmount() {},
};
</script>

<style lang="scss" scoped>
.header-section {
  text-align: center;
}

.black-header {
  background-color: black;
}

.v-stepper {
  box-shadow: none;
  background: transparent;
}

.v-stepper__header {
  box-shadow: none;
  justify-content: space-between;

  .v-stepper__step {
    :deep(.v-stepper__label) {
      text-align: center;
      display: block;
      width: 100%;
    }
  }
}

.info-section {
  .restaurant-detail-item {
    margin-bottom: 32px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.overview-card {
  border-radius: 8px;

  .overview-title {
    border-bottom: 2px solid #000;
    margin: 0;
    padding-bottom: 12px;
  }
}

// New styles to match PdfContent
.product-variation {
  font-size: 0.8rem;
  font-weight: 800;
}

.product-addons-list {
  font-size: 0.8rem;
}

.divider-details {
  padding-top: 1px;
}

// Make text size consistent
span {
  font-size: 0.8rem;
}

// Make total and important text bold
.font-weight-bold span {
  font-weight: 800;
}

.sub-addons-container {
  padding-left: 15px;
}

.step_title {
  display: block;
  font-weight: 500;
  font-size: 0.875rem;
}

.step_description {
  display: block;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
}

@media (max-width: 600px) {
  .steps-container {
    .step_title {
      font-size: 0.75rem;
    }
  }
}
</style>
